import type { BottomNavigationAction, BottomNavigationDrawerAction } from "@bonusx-global/bx-design-system";
import { BottomNavigation, BottomNavigationDrawer } from "@bonusx-global/bx-design-system";
import { memo, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PRICING_PAGE_PATH } from "../../../constants/env";
import useIsMobile from "../../../hooks/device/useIsMobile";
import { useBottomNavigationDrawerOpen } from "../../../stores/bottom-navigation-drawer.store";
import { useCurrentUserSubscriptionTier } from "../../hooks/use-current-user-subscription-tier/use-current-user-subscription-tier";
import { useMutateSearch } from "../../hooks/useSearchModule";
import { BONUSX_SUBSCRIPTION_FREQUENCIES } from "../../services/user-subscription/enums/bonusx-subscription-frequencies.enum";
import { BONUSX_SUBSCRIPTION_TIERS } from "../../services/user-subscription/enums/bonusx-subscription-tiers.enum";
import { BonusXThemeProvider } from "../../theme/BonusXThemeProvider";
import { EmptyComponent } from "../common/EmptyComponent";
import { BOTTOM_NAV_BAR_ID, BOTTOM_NAV_DRAWER_ID } from "./const/bottom-navigation-ids";
import { BOTTOM_NAVIGATION_DRAWER_ROUTES, BOTTOM_NAVIGATION_ROUTES } from "./const/bottom-navigation-routes";
import { enrichRouteWithClickHandler } from "./functions/enrich-route-with-click-handler/enrich-route-with-click-handler";
import { enrichRouteWithSelectedStatus } from "./functions/enrich-route-with-selected-status/enrich-route-with-selected-status";
import { isDrawerTogglerPath } from "./functions/is-drawer-toggler-path/is-drawer-toggler-path";
import { isPathExternal } from "./functions/is-path-external/is-path-external";
import { openRoutePath } from "./functions/open-route-path/open-route-path";

const BOTTOM_NAVBAR_ZINDEX = 12;

function BottomNavigationBar(): JSX.Element {
  const isMobile = useIsMobile();
  const mutateSearch = useMutateSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUserTier, currentUserTierFrequency } = useCurrentUserSubscriptionTier();

  const isCurrentUserPlanTier3AndYearly = useMemo(
    function memoizeIsCurrentUserPlanTier3AndYearly() {
      return (
        currentUserTier === BONUSX_SUBSCRIPTION_TIERS.TIER_3 &&
        currentUserTierFrequency === BONUSX_SUBSCRIPTION_FREQUENCIES.YEARLY
      );
    },
    [currentUserTier, currentUserTierFrequency]
  );

  const [isDrawerOpen, setIsDrawerOpen] = useBottomNavigationDrawerOpen();

  const onClickRouteCallback = useCallback(
    function onClickRoute(path?: string) {
      if (!path) {
        return;
      }

      if (isDrawerTogglerPath(path)) {
        setIsDrawerOpen(!isDrawerOpen);
        return;
      }

      openRoutePath({
        path,
        navigate,
        currentPath: location.pathname,
      });
      mutateSearch({ visible: false, searchText: "" });

      if (!isPathExternal(path)) {
        setIsDrawerOpen(false);
      }
    },
    [navigate, isDrawerOpen, location.pathname]
  );

  const bottomNavigationDrawerRoutesWithHandlersAndSelectedStatus = useMemo(
    function addCallbackAndSelectedStatusToDrawerRoutes() {
      return BOTTOM_NAVIGATION_DRAWER_ROUTES.map((route: BottomNavigationDrawerAction) => {
        const routeWithCallback = enrichRouteWithClickHandler({
          route,
          callback: onClickRouteCallback,
        });
        const routeWithSelectedStatusAndCallback = enrichRouteWithSelectedStatus({
          route: routeWithCallback,
          currentPathName: location.pathname,
        });
        return routeWithSelectedStatusAndCallback as BottomNavigationDrawerAction;
      });
    },
    [location.pathname]
  );

  const bottomNavigationRoutesWithHandlersAndSelectedStatus = useMemo(
    function addCallbackAndSelectedStatusToMainRoutes() {
      const routes = isCurrentUserPlanTier3AndYearly
        ? BOTTOM_NAVIGATION_ROUTES.filter(function filterUpgradeRoute(route) {
            return route.path !== PRICING_PAGE_PATH;
          })
        : BOTTOM_NAVIGATION_ROUTES;

      return routes.map((route: BottomNavigationAction) => {
        const routeWithCallback = enrichRouteWithClickHandler({
          route,
          callback: onClickRouteCallback,
        });

        const shouldDrawerBeSelected =
          isDrawerOpen || bottomNavigationDrawerRoutesWithHandlersAndSelectedStatus.some((route) => route.selected);

        const routeWithSelectedStatusAndCallback = enrichRouteWithSelectedStatus({
          route: routeWithCallback,
          currentPathName: location.pathname,
          isDrawerRouteSelected: shouldDrawerBeSelected,
        });

        return routeWithSelectedStatusAndCallback as BottomNavigationAction;
      });
    },
    [
      location.pathname,
      bottomNavigationDrawerRoutesWithHandlersAndSelectedStatus,
      isDrawerOpen,
      isCurrentUserPlanTier3AndYearly,
    ]
  );

  if (!isMobile) {
    return <EmptyComponent />;
  }

  return (
    <BonusXThemeProvider>
      <BottomNavigation
        id={BOTTOM_NAV_BAR_ID}
        items={bottomNavigationRoutesWithHandlersAndSelectedStatus}
        sx={{ zIndex: BOTTOM_NAVBAR_ZINDEX }}
      />
      <BottomNavigationDrawer
        id={BOTTOM_NAV_DRAWER_ID}
        items={bottomNavigationDrawerRoutesWithHandlersAndSelectedStatus}
        open={isDrawerOpen}
        sx={{ zIndex: BOTTOM_NAVBAR_ZINDEX - 1 }}
        onClose={() => setIsDrawerOpen(false)}
        onOpen={() => setIsDrawerOpen(true)}
      />
    </BonusXThemeProvider>
  );
}

export default memo(BottomNavigationBar);
